import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

/**
 * UI Store for global UI state
 * Per State-Management.md Section 6.2 and App-Shell.md Section 2
 */

type Theme = 'light' | 'dark' | 'system'
type Workspace = 'operations' | 'finance' | 'sales'
export type TableDensity = 'comfortable' | 'compact'

/**
 * State for the most recent permission-denial (403) the user encountered.
 * Per RBAC Module spec #10 §3.6 — drives the PermissionDeniedToast.
 */
export interface PermissionDenial {
  message: string
  requiredPermission: string | null
  errorCode: string | null
  /** Composite dedup key: `${url}:${errorCode}:${requiredPermission}` */
  dedupKey: string
  /** Timestamp this denial was raised (epoch ms). */
  raisedAt: number
}

interface UIState {
  // Sidebar state
  sidebarCollapsed: boolean
  sidebarMobileOpen: boolean

  // Theme
  theme: Theme

  // Workspace mode (Operations vs Finance)
  workspace: Workspace

  // Command palette
  commandPaletteOpen: boolean

  // Sidebar section expand states (for nested nav items)
  expandedSections: Record<string, boolean>

  // Job order list table density preference (persisted)
  jobOrderTableDensity: TableDensity

  // Job order list filter bar visibility — null means "follow density default"
  // (visible in comfortable, hidden in compact). Once user explicitly toggles, this becomes a boolean.
  jobOrderFiltersVisible: boolean | null

  // Quotation list table density preference (persisted)
  quoteTableDensity: TableDensity

  // Quotation list filter bar visibility — null means "follow density default"
  quoteFiltersVisible: boolean | null

  // RBAC v1 — most recent permission denial (drives PermissionDeniedToast).
  permissionDenial: PermissionDenial | null

  // Recently-shown denials, keyed by `dedupKey` → timestamp. Used to dedup
  // repeated 403s within the 5s window (per spec #10 §5.10).
  recentDenials: Record<string, number>

  // Actions
  toggleSidebar: () => void
  setSidebarCollapsed: (collapsed: boolean) => void
  setSidebarMobileOpen: (open: boolean) => void
  setTheme: (theme: Theme) => void
  setWorkspace: (workspace: Workspace) => void
  setCommandPaletteOpen: (open: boolean) => void
  toggleSection: (section: string) => void
  setSectionExpanded: (section: string, expanded: boolean) => void
  collapseAllSections: () => void
  setJobOrderTableDensity: (density: TableDensity) => void
  setJobOrderFiltersVisible: (visible: boolean | null) => void
  setQuoteTableDensity: (density: TableDensity) => void
  setQuoteFiltersVisible: (visible: boolean | null) => void
  /**
   * Show a permission-denied toast. Deduplicates within 5s on `{url, errorCode, requiredPermission}`.
   * Returns true if the toast was raised, false if suppressed by dedup.
   */
  showPermissionDenied: (denial: Omit<PermissionDenial, 'dedupKey' | 'raisedAt'> & { url: string }) => boolean
  dismissPermissionDenied: () => void
}

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      // Initial state - per App-Shell.md, sidebar expanded by default on desktop
      sidebarCollapsed: false,
      sidebarMobileOpen: false,
      theme: 'light',
      workspace: 'operations',
      commandPaletteOpen: false,
      expandedSections: {
        jobOrders: false,
        masterData: false,
        finance: false,
        financeTransactions: false,
        financeAccounts: false,
      },
      jobOrderTableDensity: 'comfortable',
      jobOrderFiltersVisible: null,
      quoteTableDensity: 'comfortable',
      quoteFiltersVisible: null,
      permissionDenial: null,
      recentDenials: {},

      // Toggle sidebar collapsed state
      toggleSidebar: () =>
        set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),

      // Set sidebar collapsed directly
      setSidebarCollapsed: (collapsed) =>
        set({ sidebarCollapsed: collapsed }),

      // Set mobile sidebar open state
      setSidebarMobileOpen: (open) =>
        set({ sidebarMobileOpen: open }),

      // Set theme
      setTheme: (theme) => set({ theme }),

      // Set workspace mode
      setWorkspace: (workspace) => set({ workspace }),

      // Set command palette open state
      setCommandPaletteOpen: (open) =>
        set({ commandPaletteOpen: open }),

      // Toggle a nav section expanded state (accordion behavior for finance sections)
      toggleSection: (section) =>
        set((state) => {
          const isExpanding = !state.expandedSections[section]
          const financeSections = ['financeTransactions', 'financeAccounts']

          // If expanding a finance section, close all other finance sections (accordion)
          if (isExpanding && financeSections.includes(section)) {
            const newExpandedSections = { ...state.expandedSections }
            financeSections.forEach((s) => {
              newExpandedSections[s] = s === section
            })
            return { expandedSections: newExpandedSections }
          }

          // Otherwise just toggle the section
          return {
            expandedSections: {
              ...state.expandedSections,
              [section]: isExpanding,
            },
          }
        }),

      // Set section expanded directly
      setSectionExpanded: (section, expanded) =>
        set((state) => ({
          expandedSections: {
            ...state.expandedSections,
            [section]: expanded,
          },
        })),

      // Set job order table density preference
      setJobOrderTableDensity: (density) =>
        set({ jobOrderTableDensity: density }),

      // Set job order filter bar visibility (null = follow density default)
      setJobOrderFiltersVisible: (visible) =>
        set({ jobOrderFiltersVisible: visible }),

      // Set quotation table density preference
      setQuoteTableDensity: (density) =>
        set({ quoteTableDensity: density }),

      // Set quotation filter bar visibility (null = follow density default)
      setQuoteFiltersVisible: (visible) =>
        set({ quoteFiltersVisible: visible }),

      // Collapse all expanded sections (called when navigating to a non-expandable item)
      collapseAllSections: () =>
        set((state) => {
          const newExpandedSections = { ...state.expandedSections }
          Object.keys(newExpandedSections).forEach((key) => {
            newExpandedSections[key] = false
          })
          return { expandedSections: newExpandedSections }
        }),

      // RBAC v1 — show a permission-denied toast with 5s dedup (spec #10 §5.10)
      showPermissionDenied: ({ url, message, requiredPermission, errorCode }) => {
        const dedupKey = `${url}:${errorCode ?? '-'}:${requiredPermission ?? '-'}`
        const now = Date.now()
        const recent = useUIStore.getState().recentDenials[dedupKey]
        if (recent && now - recent < 5000) {
          return false  // suppressed by dedup window
        }
        set((state) => ({
          permissionDenial: {
            message,
            requiredPermission,
            errorCode,
            dedupKey,
            raisedAt: now
          },
          recentDenials: {
            ...state.recentDenials,
            [dedupKey]: now
          }
        }))
        return true
      },

      dismissPermissionDenied: () => set({ permissionDenial: null }),
    }),
    {
      name: 'freighai-ui',
      storage: createJSONStorage(() => localStorage),
      // Only persist these fields
      partialize: (state) => ({
        sidebarCollapsed: state.sidebarCollapsed,
        theme: state.theme,
        workspace: state.workspace,
        expandedSections: state.expandedSections,
        jobOrderTableDensity: state.jobOrderTableDensity,
        jobOrderFiltersVisible: state.jobOrderFiltersVisible,
        quoteTableDensity: state.quoteTableDensity,
        quoteFiltersVisible: state.quoteFiltersVisible,
      }),
    }
  )
)
